
import React, { useState, useRef, useEffect, KeyboardEvent } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    Button,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tooltip,
    Dialog as MuiDialog,
    DialogTitle as MuiDialogTitle,
    DialogContent as MuiDialogContent,
    DialogActions,
    Typography,
    Box,
    Snackbar,
    Alert,
    Checkbox,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit,
    Cancel as CancelIcon,
    Download as DownloadIcon,
    Error as ErrorIcon,
} from '@mui/icons-material';
import { saveAs } from 'file-saver';
import { GripVertical } from 'lucide-react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { dutyItemsTemplate } from 'core/API/checklist';


// Define the structure of a checklist item
interface ChecklistItem {
    id: string;
    section: string;
    duty: string;
    instructions: string;
    answerType: string;
    correctAnswer: string;
    isEditing: boolean;
    isSaved: boolean;
    isSelected: boolean;
    isValid: {
        section: boolean;
        duty: boolean;
        instructions: boolean;
        answerType: boolean;
        correctAnswer: boolean;
    };
    errors: {
        section?: string;
        duty?: string;
        instructions?: string;
        answerType?: string;
        correctAnswer?: string;
    };
}

// Define column validation rules
interface ValidationRule {
    required?: boolean;
    minLength?: number;
    maxLength?: number;
    pattern?: RegExp;
    patternMessage?: string;
    options?: string[];
    dynamicOptions?: (item: ChecklistItem) => string[];
}
// Draggable Row Component
interface DraggableRowProps {
    item: ChecklistItem;
    index: number;
    moveRow: (dragIndex: number, hoverIndex: number) => void;
    toggleRowSelection: (id: string) => void;
    columns: { id: string; header: string; validation?: ValidationRule }[];
    cellRefs: React.MutableRefObject<{ [key: string]: HTMLInputElement | HTMLTextAreaElement | null }>;
    handleCellFocus: (rowIndex: number, colId: string) => void;
    handleKeyDown: (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>, rowIndex: number, colId: string) => void;
    updateField: (id: string, field: string, value: string) => void;
    toggleEditMode: (id: string) => void;
    deleteRow: (id: string) => void;
    handleCancelClick: (id: string) => void;
    headerdata?: any;
}

// Define drag item type for react-dnd
const ItemTypes = {
    ROW: 'row',
};

export const  DraggableRow: React.FC<DraggableRowProps> = ({
    item,
    index,
    moveRow,
    toggleRowSelection,
    columns,
    cellRefs,
    handleCellFocus,
    handleKeyDown,
    updateField,
    toggleEditMode,
    deleteRow,
    handleCancelClick,
    headerdata,
}) => {
    const ref = useRef<HTMLTableRowElement>(null);

    const [{ isDragging }, drag, preview] = useDrag({
        type: ItemTypes.ROW,
        item: { index },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const [, drop] = useDrop({
        accept: ItemTypes.ROW,
        hover: (draggedItem: { index: number }) => {
            if (draggedItem.index !== index) {
                moveRow(draggedItem.index, index);
                draggedItem.index = index;
            }
        },
    });

    drag(drop(ref));
    
    return (
        <TableRow
            ref={ref}
            hover
            selected={item.isSelected}
            sx={{
                '&.Mui-selected': { backgroundColor: 'rgba(25, 118, 210, 0.08)' },
                '&.Mui-selected:hover': { backgroundColor: 'rgba(25, 118, 210, 0.12)' },
                opacity: isDragging ? 0.5 : 1,
                cursor: 'grab',
            }}
        >
            <TableCell
                padding="checkbox"
                style={{
                    width: '70px',
                    height: '65px',
                    // display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <GripVertical
                    style={{
                        cursor: 'grab',
                        color: 'rgb(68, 99, 136)',
                    }}
                    ref={drag}
                />
            </TableCell>
            {columns.map((column) => (
                <TableCell key={`${item.id}-${column.id}`} sx={{ p: 1 }}>
                    {(!headerdata || headerdata === '') || !['answerType', 'correctAnswer'].includes(column.id) ? (
                        item.isEditing ? (
                            <Box sx={{ position: 'relative' }}>
                                {column.validation?.options || column.validation?.dynamicOptions ? (
                                    <FormControl
                                        fullWidth
                                        size="small"
                                        error={!item.isValid[column.id as keyof typeof item.isValid]}
                                    >
                                        <Select
                                            value={item[column.id as keyof ChecklistItem] as string}
                                            onChange={(e) => updateField(item.id, column.id, e.target.value)}
                                            inputRef={(el) => {
                                                cellRefs.current[`${index}-${column.id}`] = el;
                                            }}
                                            onFocus={() => handleCellFocus(index, column.id)}
                                            onKeyDown={(e) => handleKeyDown(e as any, index, column.id)}
                                            sx={{
                                                height: '32px',
                                                '& .MuiSelect-select': {
                                                    padding: '6px 32px 6px 12px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                },
                                            }}
                                        >
                                            {column.id === 'answerType' &&
                                                column.validation?.options?.map((option) => (
                                                    <MenuItem key={option} value={option}>
                                                        {option}
                                                    </MenuItem>
                                                ))}
                                            {column.id === 'correctAnswer' &&
                                                column.validation?.dynamicOptions?.(item)?.map((option) => (
                                                    <MenuItem key={option} value={option}>
                                                        {option}
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                    </FormControl>
                                ) : (
                                    <textarea
                                        value={item[column.id as keyof ChecklistItem] as string}
                                        onChange={(e) => updateField(item.id, column.id, e.target.value)}
                                        onFocus={() => handleCellFocus(index, column.id)}
                                        onKeyDown={(e) => handleKeyDown(e as any, index, column.id)}
                                        placeholder={column.header}
                                        ref={(el) => {
                                            cellRefs.current[`${index}-${column.id}`] = el;
                                        }}
                                        style={{
                                            width: '100%',
                                            minHeight: '60px',
                                            maxHeight: '120px',
                                            padding: '8px 12px',
                                            border: !item.isValid[column.id as keyof typeof item.isValid] 
                                                ? '1px solid #d32f2f' 
                                                : '1px solid #c4c4c4',
                                            borderRadius: '4px',
                                            fontSize: '14px',
                                            fontFamily: 'inherit',
                                            resize: 'vertical',
                                            boxSizing: 'border-box',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            lineHeight: '1.4',
                                            outline: 'none',
                                        }}
                                    />
                                )}
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: !item.isValid[column.id as keyof typeof item.isValid]
                                            ? 'error.main'
                                            : 'inherit',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                    }}
                                >
                                    {item[column.id as keyof ChecklistItem] as string}
                                </Typography>
                                {!item.isValid[column.id as keyof typeof item.isValid] && (
                                    <Tooltip title={item.errors[column.id as keyof typeof item.errors] || 'Invalid value'}>
                                        <ErrorIcon color="error" fontSize="small" sx={{ ml: 1 }} />
                                    </Tooltip>
                                )}
                            </Box>
                        )
                    ) : null}
                </TableCell>
            ))}
            {(!headerdata || headerdata === '') && (
                <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {item.isEditing ? (
                            <Tooltip title="Cancel">
                                <IconButton size="small" color="default" onClick={() => handleCancelClick(item.id)}>
                                    <CancelIcon fontSize="small" style={{ color: 'red' }} />
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <>
                                <Tooltip title="Edit">
                                    <IconButton size="small" color="primary" onClick={() => toggleEditMode(item.id)}>
                                        <Edit fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                    <IconButton size="small" color="error" onClick={() => deleteRow(item.id)}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                    </Box>
                </TableCell>
            )}
        </TableRow>
    );
};